export const TABLE_QR_BASE = 'https://app.tablemate.net/open/table';

export function buildTableQrUrl(restaurantId, tableNumber) {
  const rid = String(restaurantId ?? '').trim();
  const tnum = String(tableNumber ?? '').trim();
  if (!rid || !tnum) return '';
  return `${TABLE_QR_BASE}?restaurantID=${encodeURIComponent(rid)}&tableNumber=${encodeURIComponent(tnum)}`;
}

export function tableQrFilename(table) {
  const number = String(table?.number ?? table?.id ?? 'unknown').trim();
  const nickname = String(table?.nickname ?? table?.name ?? '').trim();
  const safeNickname = nickname.replace(/[^\w\-]+/g, '_').slice(0, 40);
  const suffix = safeNickname ? `_${safeNickname}` : '';
  return `table_${number}${suffix}.png`;
}

function dataUrlToBase64(dataUrl) {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/**
 * Generates PNG QR codes for every table and downloads them as a zip file.
 * @returns {Promise<number>} number of QR images written to the zip
 */
export async function exportTableQrCodesZip({ tables, restaurantId, zipFilename }) {
  if (!Array.isArray(tables) || tables.length === 0) {
    throw new Error('No tables to export.');
  }

  const rid = String(restaurantId ?? '').trim();
  if (!rid) {
    throw new Error('Restaurant ID is required to export QR codes.');
  }

  const QRCode = (await import('qrcode')).default;
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const usedNames = new Set();
  let count = 0;

  for (const table of tables) {
    const tableNumber = table?.number ?? table?.id;
    const qrUrl = buildTableQrUrl(rid, tableNumber);
    if (!qrUrl) continue;

    const dataUrl = await QRCode.toDataURL(qrUrl, { margin: 1, scale: 8, width: 512 });
    let filename = tableQrFilename(table);
    if (usedNames.has(filename)) {
      filename = `table_${String(tableNumber).trim()}_${String(table?.id ?? count).trim()}.png`;
    }
    usedNames.add(filename);

    zip.file(filename, dataUrlToBase64(dataUrl), { base64: true });
    count += 1;
  }

  if (count === 0) {
    throw new Error('No tables with valid numbers to export.');
  }

  const manifest = tables
    .map((table) => {
      const tableNumber = table?.number ?? table?.id;
      const qrUrl = buildTableQrUrl(rid, tableNumber);
      if (!qrUrl) return null;
      const label = table?.nickname ?? table?.name ?? `Table ${tableNumber}`;
      return `${label} (table ${tableNumber})\n${qrUrl}\nFile: ${tableQrFilename(table)}`;
    })
    .filter(Boolean)
    .join('\n\n');

  zip.file('README.txt', [
    'Table Mate — table QR codes',
    `Restaurant ID: ${rid}`,
    `Generated: ${new Date().toISOString()}`,
    '',
    manifest,
  ].join('\n'));

  const blob = await zip.generateAsync({ type: 'blob' });
  const name = zipFilename || `table_qr_codes_restaurant_${rid}.zip`;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(objectUrl);

  return count;
}
